import React, { Component } from 'react';
import { Badge, Button, Dialog, Heading, Pane, Select, Spinner, Table, Text, toaster } from 'evergreen-ui';
import moment from 'moment';

import Request from '../../../util/Api';
import { StockOperationTypes } from '../models/StockOperationTypes';
import { StockOperationLabels } from '../models/StockOperationLabels';
import { StockOperationStatuses } from '../models/StockOperationStatuses';

const fmtDate = (d) => (d ? moment(d).format('DD.MM.YYYY') : '—');
const isProcessed = (op) => op.committed === 1 || op.status === 'processed';
const survivorOf = (ops) => ops.reduce((a, b) => (a.id < b.id ? a : b), ops[0]);

const emptyState = {
    phase: 'select',
    search: '',
    typeFilter: '',
    dateFrom: '',
    dateTo: '',
    results: [],
    loading: false,
    selected: [],
    keep: {},
    preview: null,
    confirmStep: 0,
    merging: false,
    previewId: null,
    previews: {},
    previewLoadingId: null,
};

class MergeModal extends Component {
    constructor(props) {
        super(props);
        this.state = { ...emptyState };
        this.searchTimer = null;
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState({ ...emptyState }, () => this.doSearch(''));
        }
    }

    // --- lookups -----------------------------------------------------------
    supplierName = (id) => {
        const s = (this.props.suppliers || []).find((x) => x.id === Number(id));
        return s ? s.name : (id ? `Furnizor #${id}` : '—');
    };
    stockAccountName = (id) => {
        const s = (this.props.stockAccounts || []).find((x) => x.id === Number(id));
        return s ? s.name : (id ? `Gestiune #${id}` : '—');
    };
    typeLabel = (type) => {
        const t = StockOperationTypes.find((x) => x.id === type);
        return t ? `${t.labelEx} (${t.id})` : (type || '—');
    };
    labelLabel = (label) => {
        const l = StockOperationLabels.find((x) => x.id === label);
        return l ? l.label : (label || '—');
    };
    statusLabel = (status) => {
        const s = StockOperationStatuses.find((x) => x.id === status);
        return s ? s.label : (status || '—');
    };

    keepFields = () => [
        { key: 'date', label: 'Data Livrare', fmt: (op) => fmtDate(op.date) },
        { key: 'documentName', label: 'Numar Factura / Document', fmt: (op) => op.documentName || '—' },
        { key: 'documentDate', label: 'Data Factura', fmt: (op) => fmtDate(op.documentDate) },
        { key: 'supplierId', label: 'Furnizor', fmt: (op) => this.supplierName(op.supplierId) },
        { key: 'stockAccountId', label: 'Gestiune', fmt: (op) => this.stockAccountName(op.stockAccountId) },
        { key: 'type', label: 'Serie', fmt: (op) => this.typeLabel(op.type) },
        { key: 'typeDetails', label: 'Detalii', fmt: (op) => op.typeDetails || '—' },
        { key: 'currency', label: 'Moneda', fmt: (op) => op.currency || '—' },
        { key: 'exchange', label: 'Curs', fmt: (op) => String(op.exchange != null ? op.exchange : '—') },
        { key: 'label', label: 'Eticheta', fmt: (op) => this.labelLabel(op.label) },
    ];

    defaultKeep = (ops) => {
        if (!ops.length) return {};
        const s = survivorOf(ops);
        const keep = {};
        this.keepFields().forEach(({ key }) => {
            keep[key] = s[key] != null ? String(s[key]) : '';
        });
        return keep;
    };

    // --- search & selection ------------------------------------------------
    doSearch = async (q = this.state.search, type = this.state.typeFilter, dateFrom = this.state.dateFrom, dateTo = this.state.dateTo) => {
        this.setState({ loading: true });
        try {
            const filters = {};
            if (type) filters.type = type;
            if (dateFrom) filters.dateFrom = dateFrom;
            if (dateTo) filters.dateTo = dateTo;
            const params = { q, _start: 0, _end: 25, _sort: 'id', _order: 'DESC' };
            if (Object.keys(filters).length) params.extraFilters = JSON.stringify(filters);
            const res = await Request.get('/stock-operations', {}, params);
            this.setState({ results: res.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            toaster.danger(e.message || 'Cautarea a esuat');
        }
    };

    onSearchChange = (value) => {
        this.setState({ search: value });
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.doSearch(value), 300);
    };

    onTypeChange = (value) => {
        this.setState({ typeFilter: value });
        this.doSearch(this.state.search, value);
    };

    onDateChange = (field, value) => {
        this.setState({ [field]: value });
        const from = field === 'dateFrom' ? value : this.state.dateFrom;
        const to = field === 'dateTo' ? value : this.state.dateTo;
        this.doSearch(this.state.search, this.state.typeFilter, from, to);
    };

    addOp = (op) => {
        if (this.state.selected.some((o) => o.id === op.id)) return;
        const selected = [...this.state.selected, op];
        this.setState({ selected, keep: this.defaultKeep(selected) });
    };
    removeOp = (id) => {
        const selected = this.state.selected.filter((o) => o.id !== id);
        this.setState({ selected, keep: this.defaultKeep(selected) });
    };
    setKeep = (key, value) => this.setState({ keep: { ...this.state.keep, [key]: value } });

    itemName = (it) => it.name || (it.product && it.product.title) || (it.card && it.card.name) || 'Fara nume';

    togglePreview = async (op) => {
        const open = this.state.previewId === op.id ? null : op.id;
        this.setState({ previewId: open });
        if (open && !this.state.previews[op.id]) {
            this.setState({ previewLoadingId: op.id });
            try {
                const res = await Request.get('/stock-operations/[id]', { id: op.id });
                this.setState({
                    previews: { ...this.state.previews, [op.id]: (res.data && res.data.items) || [] },
                    previewLoadingId: null,
                });
            } catch (e) {
                this.setState({ previewLoadingId: null });
                toaster.danger(e.message || 'Nu am putut incarca operatia');
            }
        }
    };

    // --- preview & commit --------------------------------------------------
    buildPayload = (dryRun) => ({
        sourceIds: this.state.selected.map((o) => o.id),
        keep: this.state.keep,
        dryRun,
    });

    goPreview = async () => {
        this.setState({ loading: true });
        try {
            const res = await Request.post('/stock-operations/merge', {}, {}, this.buildPayload(true));
            this.setState({ preview: res.data, phase: 'review', confirmStep: 0, loading: false });
        } catch (e) {
            this.setState({ loading: false });
            toaster.danger(e.message || 'Previzualizarea a esuat');
        }
    };

    onCombina = () => {
        const required = this.state.preview && this.state.preview.anyProcessed ? 2 : 0;
        if (this.state.confirmStep < required) {
            this.setState({ confirmStep: this.state.confirmStep + 1 });
            return;
        }
        this.commit();
    };

    commit = async () => {
        this.setState({ merging: true });
        try {
            const res = await Request.post('/stock-operations/merge', {}, {}, this.buildPayload(false));
            toaster.success('Operatiile au fost combinate');
            this.setState({ merging: false });
            if (this.props.onMerged) this.props.onMerged(res.data);
            this.props.onClose();
        } catch (e) {
            this.setState({ merging: false });
            toaster.danger(e.message || 'Combinarea a esuat');
        }
    };

    // --- render ------------------------------------------------------------
    renderOpRow(op, action) {
        const open = this.state.previewId === op.id;
        const items = this.state.previews[op.id];
        return (
            <Pane key={op.id} marginBottom={4}>
                <Pane display="flex" alignItems="center" justifyContent="space-between"
                    paddingY={6} paddingX={8} background="tint1" borderRadius={4}>
                    <Pane>
                        <Badge color="blue">#{op.id}</Badge>
                        <Text fontWeight={500} marginLeft={8}>{this.typeLabel(op.type).split(' (')[0]}-{op.number || 'x'}</Text>
                        <Text color="muted" marginLeft={8}>{op.documentName || 'Fara document'}</Text>
                        <Text color="muted" marginLeft={8}>{fmtDate(op.date)}</Text>
                        <Text color="muted" marginLeft={8}>{this.supplierName(op.supplierId)}</Text>
                        <Badge marginLeft={8} color={isProcessed(op) ? 'red' : 'neutral'}>{this.statusLabel(op.status)}</Badge>
                    </Pane>
                    <Pane display="flex" alignItems="center" flexShrink={0}>
                        <Button appearance="minimal" marginRight={8} onClick={() => this.togglePreview(op)}>
                            {open ? 'Ascunde' : 'Vezi'}
                        </Button>
                        {action}
                    </Pane>
                </Pane>
                {open && (
                    <Pane paddingX={12} paddingY={8} marginTop={2} background="tint2" borderRadius={4}>
                        {this.state.previewLoadingId === op.id && <Spinner size={20} />}
                        {items && items.length === 0 && <Text color="muted">Nicio linie.</Text>}
                        {items && items.length > 0 && (
                            <Table>
                                <Table.Head height={28}>
                                    <Table.TextHeaderCell>Produs</Table.TextHeaderCell>
                                    <Table.TextHeaderCell flexBasis={70} flexGrow={0}>Cant.</Table.TextHeaderCell>
                                    <Table.TextHeaderCell flexBasis={110} flexGrow={0}>Valoare</Table.TextHeaderCell>
                                </Table.Head>
                                <Table.Body maxHeight={220} overflowY="auto">
                                    {items.map((it, i) => (
                                        <Table.Row key={i} height="auto" paddingY={4}>
                                            <Table.TextCell>{this.itemName(it)}</Table.TextCell>
                                            <Table.TextCell flexBasis={70} flexGrow={0}>{it.quantity}</Table.TextCell>
                                            <Table.TextCell flexBasis={110} flexGrow={0}>{it.adjustedValue}</Table.TextCell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                    </Pane>
                )}
            </Pane>
        );
    }

    renderSelect() {
        const { selected, results, loading, keep, search } = this.state;
        const selectedIds = new Set(selected.map((o) => o.id));
        const visibleResults = results.filter((o) => !selectedIds.has(o.id));

        return (
            <Pane>
                <Pane display="flex" alignItems="center" flexWrap="wrap" marginBottom={12}>
                    <input
                        value={search}
                        placeholder="Cauta dupa numar factura / document..."
                        onChange={(e) => this.onSearchChange(e.target.value)}
                        style={{ flex: 1, minWidth: 200, padding: 8, boxSizing: 'border-box' }}
                    />
                    <Select width={220} marginLeft={8} value={this.state.typeFilter}
                        onChange={(e) => this.onTypeChange(e.target.value)}>
                        <option value="">Toate seriile</option>
                        {StockOperationTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.labelEx} ({t.id})</option>
                        ))}
                    </Select>
                    <Text size={300} color="muted" marginLeft={12} marginRight={4}>Livrare:</Text>
                    <input type="date" value={this.state.dateFrom} title="Data livrare de la"
                        onChange={(e) => this.onDateChange('dateFrom', e.target.value)}
                        style={{ padding: 7, boxSizing: 'border-box' }} />
                    <input type="date" value={this.state.dateTo} title="Data livrare pana la"
                        onChange={(e) => this.onDateChange('dateTo', e.target.value)}
                        style={{ marginLeft: 8, padding: 7, boxSizing: 'border-box' }} />
                </Pane>

                {selected.length > 0 && (
                    <Pane marginBottom={12}>
                        <Heading size={300} marginBottom={6}>Selectate ({selected.length})</Heading>
                        {selected.map((op) => this.renderOpRow(op,
                            <Button intent="danger" appearance="minimal" onClick={() => this.removeOp(op.id)}>Scoate</Button>))}
                    </Pane>
                )}

                {selected.length >= 2 && (
                    <Pane marginBottom={12} padding={10} background="tint2" borderRadius={4}>
                        <Heading size={300} marginBottom={8}>Ce pastreaza operatia combinata</Heading>
                        <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={10}>
                            {this.keepFields().map(({ key, label, fmt }) => {
                                const seen = new Map();
                                selected.forEach((op) => {
                                    const v = op[key] != null ? String(op[key]) : '';
                                    if (!seen.has(v)) seen.set(v, fmt(op));
                                });
                                return (
                                    <Pane key={key}>
                                        <Text size={300} color="muted">{label}</Text>
                                        <Select width="100%" value={keep[key] || ''} onChange={(e) => this.setKeep(key, e.target.value)}>
                                            {[...seen.entries()].map(([v, l]) => (
                                                <option key={v} value={v}>{l}</option>
                                            ))}
                                        </Select>
                                    </Pane>
                                );
                            })}
                        </Pane>
                    </Pane>
                )}

                <Heading size={300} marginBottom={6}>Rezultate</Heading>
                {loading && <Spinner size={24} />}
                {!loading && visibleResults.length === 0 && <Text color="muted">Niciun rezultat.</Text>}
                {!loading && visibleResults.map((op) => this.renderOpRow(op,
                    <Button appearance="primary" onClick={() => this.addOp(op)}>Adauga</Button>))}
            </Pane>
        );
    }

    renderReview() {
        const { preview, confirmStep } = this.state;
        const h = preview.header;
        return (
            <Pane>
                <Heading size={400} marginBottom={8}>Previzualizare operatie combinata</Heading>
                <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={6} marginBottom={12} padding={10} background="tint2" borderRadius={4}>
                    <Text><b>Serie:</b> {this.typeLabel(h.type)}</Text>
                    <Text><b>Data livrare:</b> {fmtDate(h.date)}</Text>
                    <Text><b>Numar factura:</b> {h.documentName || '—'}</Text>
                    <Text><b>Data factura:</b> {fmtDate(h.documentDate)}</Text>
                    <Text><b>Furnizor:</b> {this.supplierName(h.supplierId)}</Text>
                    <Text><b>Gestiune:</b> {this.stockAccountName(h.stockAccountId)}</Text>
                    <Text><b>Moneda:</b> {h.currency} (curs {h.exchange})</Text>
                    <Text><b>Eticheta:</b> {this.labelLabel(h.label)}</Text>
                </Pane>

                <Text color="muted">
                    {preview.items.length} linii · {preview.totalQuantity} buc · valoare {Math.round(preview.totalValue * 100) / 100} {h.currency}.
                    {' '}Se sterg operatiile: {preview.deletedIds.join(', ')}.
                </Text>

                <Table marginTop={10} maxHeight={260} overflowY="auto">
                    <Table.Head>
                        <Table.TextHeaderCell>Produs</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={70} flexGrow={0}>Cant.</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={110} flexGrow={0}>Valoare unit.</Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body>
                        {preview.items.map((it, i) => (
                            <Table.Row key={i} height="auto" paddingY={6}>
                                <Table.TextCell>{it.name}</Table.TextCell>
                                <Table.TextCell flexBasis={70} flexGrow={0}>{it.quantity}</Table.TextCell>
                                <Table.TextCell flexBasis={110} flexGrow={0}>{it.adjustedValue}</Table.TextCell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>

                {preview.anyProcessed && (
                    <Pane marginTop={12} padding={10} background="#FDF4F4" border="1px solid #EE9191" borderRadius={4}>
                        <Text color="danger" fontWeight={600}>
                            Atentie: operatii PROCESATE (stocul este deja inregistrat).
                        </Text>
                        {confirmStep >= 1 && (
                            <Text display="block" color="danger" marginTop={6}>
                                Combinarea reataseaza intrarile de stoc la operatia pastrata si sterge celelalte operatii.
                            </Text>
                        )}
                        {confirmStep >= 2 && (
                            <Text display="block" color="danger" fontWeight={700} marginTop={6}>
                                Confirmare finala — actiunea nu poate fi anulata.
                            </Text>
                        )}
                    </Pane>
                )}
            </Pane>
        );
    }

    render() {
        const { phase, selected, preview, confirmStep, loading, merging } = this.state;
        const inReview = phase === 'review' && preview;

        let combinaLabel = 'Combina';
        if (inReview && preview.anyProcessed) {
            combinaLabel = confirmStep === 0 ? 'Combina (operatii procesate)'
                : confirmStep === 1 ? 'Apasa pentru a confirma' : 'Combina definitiv';
        }

        return (
            <Dialog
                isShown={this.props.open}
                title="Combina operatii de stoc"
                width={920}
                hasFooter={false}
                onCloseComplete={this.props.onClose}
                shouldCloseOnOverlayClick={false}>
                {inReview ? this.renderReview() : this.renderSelect()}

                <Pane marginTop={16} display="flex" justifyContent="flex-end">
                    <Button marginRight={10} onClick={this.props.onClose}>Inchide</Button>
                    {inReview ? (
                        <>
                            <Button marginRight={10} onClick={() => this.setState({ phase: 'select', confirmStep: 0 })}>Inapoi</Button>
                            <Button appearance="primary" intent={preview.anyProcessed ? 'danger' : 'success'}
                                isLoading={merging} onClick={this.onCombina}>{combinaLabel}</Button>
                        </>
                    ) : (
                        <Button appearance="primary" intent="success" isLoading={loading}
                            disabled={selected.length < 2} onClick={this.goPreview}>
                            Previzualizare ({selected.length})
                        </Button>
                    )}
                </Pane>
            </Dialog>
        );
    }
}

export default MergeModal;
